/**
 * React hook for Google Drive Picker integration.
 *
 * Dynamically loads the Google API script and Google Identity Services,
 * handles OAuth token acquisition, and opens the Google Picker configured
 * to show only PDF files.
 */
import { useRef, useCallback, useState } from "react";
import Config from "../_config/config";

// Extend Window to include Google API globals
declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

export interface PickerResult {
  fileId: string;
  fileName: string;
  mimeType: string;
  accessToken: string;
}

// Track loading state across hook instances (scripts are global singletons)
let gapiLoaded = false;
let gisLoaded = false;
let gapiLoadPromise: Promise<void> | null = null;
let gisLoadPromise: Promise<void> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureGapiLoaded(): Promise<void> {
  if (gapiLoaded) return;
  if (!gapiLoadPromise) {
    gapiLoadPromise = loadScript("https://apis.google.com/js/api.js").then(
      () =>
        new Promise<void>((resolve) => {
          window.gapi.load("picker", () => {
            gapiLoaded = true;
            resolve();
          });
        })
    );
  }
  return gapiLoadPromise;
}

async function ensureGisLoaded(): Promise<void> {
  if (gisLoaded) return;
  if (!gisLoadPromise) {
    gisLoadPromise = loadScript(
      "https://accounts.google.com/gsi/client"
    ).then(() => {
      gisLoaded = true;
    });
  }
  return gisLoadPromise;
}

export function useGoogleDrivePicker() {
  const [loading, setLoading] = useState(false);
  const tokenRef = useRef<string | null>(null);

  const openPicker = useCallback(
    (onPicked: (result: PickerResult) => void) => {
      const clientId = Config.googleClientId;
      const apiKey = Config.googleApiKey;

      if (!clientId || !apiKey) {
        alert(
          "Google Drive is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID and REACT_APP_GOOGLE_API_KEY in .env"
        );
        return;
      }

      setLoading(true);

      Promise.all([ensureGapiLoaded(), ensureGisLoaded()])
        .then(() => {
          // If we already have a token, try to use it directly
          if (tokenRef.current) {
            showPicker(tokenRef.current, apiKey, onPicked);
            setLoading(false);
            return;
          }

          // Request a new OAuth token
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: "https://www.googleapis.com/auth/drive.readonly",
            callback: (response: any) => {
              if (response.error) {
                console.error("Google OAuth error:", response);
                setLoading(false);
                return;
              }
              tokenRef.current = response.access_token;
              showPicker(response.access_token, apiKey, onPicked);
              setLoading(false);
            },
          });

          tokenClient.requestAccessToken({ prompt: "" });
        })
        .catch((err) => {
          console.error("Failed to load Google scripts:", err);
          alert("Failed to load Google Drive integration. Please try again.");
          setLoading(false);
        });
    },
    []
  );

  return { openPicker, loading };
}

function showPicker(
  accessToken: string,
  apiKey: string,
  onPicked: (result: PickerResult) => void
) {
  const google = window.google;

  const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
    .setMimeTypes("application/pdf")
    .setMode(google.picker.DocsViewMode.LIST);

  const picker = new google.picker.PickerBuilder()
    .addView(view)
    .setOAuthToken(accessToken)
    .setDeveloperKey(apiKey)
    .setTitle("Select a PDF from Google Drive")
    .setCallback((data: any) => {
      if (data.action === google.picker.Action.PICKED) {
        const doc = data.docs[0];
        onPicked({
          fileId: doc.id,
          fileName: doc.name,
          mimeType: doc.mimeType,
          accessToken,
        });
      }
    })
    .build();

  picker.setVisible(true);
}

export default useGoogleDrivePicker;
